<template>
  <div class="modal">
    <div class="overlay" @click="closeModal"></div>
    <div class="wrapper">
      <div class="form">
        <div class="form-wrapper">
          <img class="close" @click="closeModal" src="@/assets/icons/close.svg" />
          <div class="form-fields">
            <!-- Step 1: Contact Information -->
            <div v-if="step === 1">
              <div class="title">{{ $t('CONTACT INFORMATION') }}</div>
              <div class="form-fields-wrapper">
                <FloatingLabelInput type="text" :placeholder="$t('Name')" :label="$t('Name')" v-model="name" />
                <FloatingLabelInput type="text" :placeholder="$t('Surname')" :label="$t('Surname')" v-model="surname" />
                <FloatingLabelInput
                  type="email"
                  :placeholder="$t('E-mail address')"
                  :label="$t('E-mail address')"
                  v-model="email"
                />
                <label v-if="countryOptionsIso && countryOptionsIso.length">
                  <div class="desc">{{ $t('Phone') }}</div>
                  <VuePhoneNumberInput
                    v-model="phone"
                    :only-countries="countryOptionsIso"
                    :default-country-code="defaultCountryIso"
                    @update="phoneDataUpdate($event)"
                  />
                </label>
                <label>
                  <select v-model="country" :class="{ empty: !country }">
                    <option value="" disabled selected>{{ $t('Country') }}</option>
                    <option v-for="item in countryOptions" :value="item" :key="item.id">
                      {{ item.title }}
                    </option>
                  </select>
                </label>
                <FloatingLabelInput type="text" :placeholder="$t('City')" :label="$t('City')" v-model="city" />
                <FloatingLabelInput type="text" :placeholder="$t('Address')" :label="$t('Address')" v-model="address" />
                <FloatingLabelInput
                  type="text"
                  :placeholder="$t('Post Code')"
                  :label="$t('Post Code')"
                  v-model="postCode"
                />
              </div>
              <div class="cta-container payout-method-selection">
                <div class="selection-title">{{ $t('CHOOSE PAYOUT METHOD') }}</div>
                <div class="buttons-special">
                  <button class="button" :disabled="isLoading" @click="goToIbanStep">
                    {{ $t('IBAN') }}
                  </button>
                  <button class="button red" :disabled="isLoading" @click="payoutWithCard">
                    <span v-if="!isLoading">{{ $t('Card') }}</span>
                    <Spinner v-else />
                  </button>
                </div>
              </div>
            </div>

            <!-- Step 2: IBAN Details -->
            <div v-if="step === 2">
              <div class="title">{{ $t('IBAN TRANSFER') }}</div>
              <div class="subtitle">ENYXTO LIMITED</div>
              <div class="form-fields-wrapper single-column">
                <FloatingLabelInput
                  v-if="payoutAmount"
                  type="text"
                  :placeholder="$t('Amount to receive')"
                  :label="$t('Amount to receive')"
                  :value="payoutAmount"
                  :readonly="true"
                />
                <FloatingLabelInput
                  type="text"
                  :placeholder="$t('First Name')"
                  :label="$t('First Name')"
                  v-model="bank_holder_name"
                />
                <FloatingLabelInput
                  type="text"
                  :placeholder="$t('Last Name')"
                  :label="$t('Last Name')"
                  v-model="bank_holder_surname"
                />
                <FloatingLabelInput type="text" placeholder="IBAN" label="IBAN" v-model="iban_number" maxlength="34" />
              </div>
              <div class="i-have-read-and-block">
                <label class="checkbox-label">
                  <div class="checkbox-label-wrapper">
                    <input type="checkbox" name="terms" v-model="terms" />
                    <div class="checkbox"></div>
                    <div class="checkbox-text-container">
                      <span>{{ $t('I agree with') }} </span>
                      <router-link to="/pages/privacy-policy" target="_blank">{{ $t('privacy policy') }}</router-link>
                      <span> {{ $t('and') }} </span>
                      <router-link to="/pages/terms-and-conditions" target="_blank"
                        >{{ $t('terms and conditions') }}
                      </router-link>
                    </div>
                  </div>
                </label>
              </div>
              <div class="cta-container">
                <div class="buttons-special">
                  <button class="button" @click="step = 1">{{ $t('Back') }}</button>
                  <button class="button red" :disabled="isLoading" @click="confirmIbanPayout">
                    <span v-if="!isLoading">{{ $t('Confirm') }}</span>
                    <Spinner v-else />
                  </button>
                </div>
              </div>
            </div>

            <!-- Step 3: Success Message -->
            <div v-if="step === 3">
              <div class="title">{{ $t('PAYOUT REQUEST SUCCESSFUL') }}</div>
              <div class="success-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#4CAF50"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <div class="success-text">
                {{
                  $t(
                    'Your payout request has been successfully submitted. Your funds will be transferred to your bank account soon.'
                  )
                }}
              </div>
              <div class="cta-container">
                <button class="button red" @click="closeModalAndRefresh">{{ $t('Close') }}</button>
              </div>
            </div>

            <transition name="slide">
              <div v-if="formError" class="error-desc desc red error-box">
                {{ formError }}
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions, mapState, mapGetters } from 'vuex'
  import Spinner from '@/components/shared/Spinner.vue'
  import FloatingLabelInput from '@/components/shared/FloatingLabelInput.vue'
  import VuePhoneNumberInput from 'vue-phone-number-input'
  import 'vue-phone-number-input/dist/vue-phone-number-input.css'

  export default {
    name: 'AcceptOfferModal',
    props: {
      orderToView: {
        type: Object,
        required: true
      }
    },
    components: {
      Spinner,
      FloatingLabelInput,
      VuePhoneNumberInput
    },
    data() {
      return {
        step: 1,
        name: '',
        surname: '',
        email: '',
        phone: '',
        phoneFull: '',
        country: '',
        city: '',
        address: '',
        postCode: '',

        iban_number: '',
        bank_holder_name: '',
        bank_holder_surname: '',
        terms: false,

        formError: '',
        isLoading: false
      }
    },
    computed: {
      ...mapState('user', ['user']),
      ...mapGetters('country', ['countryOptions', 'countryOptionsIso']),
      payoutAmount() {
        if (this.orderToView && this.orderToView.service_price && this.orderToView.currency) {
          return `${this.orderToView.service_price} ${this.orderToView.currency}`
        }
        return null
      },
      defaultCountryIso() {
        return this.user?.phoneCountryCode || this.countryOptionsIso?.[0];
      },
      isContactInfoValid() {
        return (
          this.name &&
          this.surname &&
          this.email &&
          this.phone &&
          this.country &&
          this.city &&
          this.address &&
          this.postCode
        )
      },
      isIbanFormValid() {
        return this.bank_holder_name && this.bank_holder_surname && this.iban_number && this.terms
      }
    },
    methods: {
      ...mapActions('modals', ['closeAcceptOfferModal']),
      ...mapActions('offer', ['requestPayoutCard', 'requestPayoutIban', 'getOfferHistory']),
      ...mapActions('country', ['getCountries']),
      ...mapActions('user', ['fetchProfile']),

      closeModal() {
        this.closeAcceptOfferModal()
        this.resetForm()
      },
      closeModalAndRefresh() {
        this.getOfferHistory()
        this.closeModal()
      },
      resetForm() {
        this.step = 1
        this.formError = ''
        this.bank_holder_name = ''
        this.bank_holder_surname = ''
        this.iban_number = ''
        this.terms = false
        this.phoneFull = ''
        if (this.user) {
          this.name = this.user.name || ''
          this.surname = this.user.surname || ''
          this.email = this.user.email || ''
          this.phone = this.user.phone || ''
        }
        this.country = ''
        this.city = ''
        this.address = ''
        this.postCode = ''
      },
      getContactInfoPayload() {
        if (!this.orderToView || !this.orderToView.id) {
          this.formError = 'Invalid offer data. Missing offer ID.'
          return null
        }

        return {
          id: this.orderToView.id,
          currency: this.orderToView.currency || 'EUR',
          name: this.name,
          surname: this.surname,
          email: this.email,
          phone: this.phoneFull || this.phone,
          country: this.country?.title || this.country?.id,
          city: this.city,
          address: this.address,
          postCode: this.postCode
        }
      },
      async payoutWithCard() {
        this.formError = ''
        if (!this.isContactInfoValid) {
          this.formError = 'Please fill in all contact fields.'
          this.$toast.error(this.formError)
          return
        }

        const data = this.getContactInfoPayload()
        if (!data) return

        this.isLoading = true
        try {
          const response = await this.requestPayoutCard(data)
          if (response && response.redirect_url) {
            window.location.href = response.redirect_url
          } else {
            this.formError = 'Could not retrieve payment URL. Please try again.'
            this.$toast.error(this.formError)
          }
        } catch (error) {
          this.handleApiError(error)
        } finally {
          this.isLoading = false
        }
      },
      async confirmIbanPayout() {
        this.formError = ''
        if (!this.isIbanFormValid) {
          this.formError = 'Please fill in all IBAN fields and accept the terms.'
          this.$toast.error(this.formError)
          return
        }

        if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4,30}$/.test(this.iban_number.replace(/\s+/g, ''))) {
          this.formError = 'Please enter a valid IBAN.'
          this.$toast.error(this.formError)
          return
        }

        if (!this.orderToView || !this.orderToView.id) {
          this.formError = 'Invalid offer data. Missing offer ID.'
          this.$toast.error(this.formError)
          return
        }

        const data = {
          id: this.orderToView.id,
          currency: this.orderToView.currency || 'EUR',
          first_name: this.bank_holder_name,
          last_name: this.bank_holder_surname,
          account_number: this.iban_number
        }

        this.isLoading = true
        try {
          const response = await this.requestPayoutIban(data)
          if (response.status === 'OK') {
            this.step = 3
            this.formError = ''
          } else {
            this.formError = response.message || 'An error occurred while processing your request.'
            this.$toast.error(this.formError)
          }
        } catch (error) {
          this.handleApiError(error)
        } finally {
          this.isLoading = false
        }
      },
      goToIbanStep() {
        this.formError = ''
        if (!this.isContactInfoValid) {
          this.formError = 'Please fill in all contact fields.'
          this.$toast.error(this.formError)
          return
        }
        this.step = 2
      },
      handleApiError(error) {
        if (error && typeof error === 'object') {
          if (error.errors) {
            // Handle field-specific errors
            let errorMessages = []
            Object.keys(error.errors).forEach((field) => {
              if (Array.isArray(error.errors[field])) {
                errorMessages = errorMessages.concat(error.errors[field])
              } else {
                errorMessages.push(error.errors[field])
              }
            })
            
            if (errorMessages.length > 0) {
              this.formError = errorMessages.join(', ')
              errorMessages.slice(0, 3).forEach((errorMessage) => {
                this.$toast.error(errorMessage)
              })
              return
            }
          }
          
          if (error.message) {
            this.formError = error.message
            this.$toast.error(error.message)
            return
          }
        }
        
        // Fallback for string errors or unknown format
        const errorMessage = typeof error === 'string' ? error : 'Something went wrong'
        this.formError = errorMessage
        this.$toast.error(errorMessage)
      },
      phoneDataUpdate(e) {
        this.phoneFull = e.formatInternational || e.phoneNumber;
      },
      async fetchInitialData() {
        try {
          await this.getCountries()
        } catch {
          this.formError = 'Could not load countries.'
          this.$toast.error(this.formError)
        }

        if (!this.user || !this.user.id) {
          await this.fetchProfile()
        }
        this.resetForm()
      }
    },
    mounted() {
      this.fetchInitialData()
    },
    watch: {
      orderToView(newVal) {
        if (newVal) {
          this.resetForm()
        }
      },
      user() {
        this.resetForm()
      }
    }
  }
</script>

<style lang="scss" scoped>
  .modal {
    ::v-deep .vue-phone-number-input .select-country-container {
      flex: 0 1 60px;
      width: 60px;
      min-width: auto;
    }
  }

  .modal .wrapper .form .form-wrapper .form-fields .title {
    text-align: center;
    text-transform: uppercase;
    font-size: 32px;
    font-weight: 700;
    margin-bottom: 12px;
  }
  
  .modal .wrapper .form .form-wrapper .form-fields .subtitle {
    text-align: center;
    font-size: 16px;
    color: #88868d;
    margin-bottom: 12px;
  }

  .form-fields-wrapper {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px 20px;

    &.single-column {
      grid-template-columns: 1fr;
      max-width: 300px;
      margin: 0 auto;
    }

    label {
      position: relative;
      height: 50px;
      display: inline-block;
      width: 100%;

      .desc {
        position: absolute;
        left: 16px;
        color: #88868d;
        font-size: 12px;
        line-height: 1.5;
        transition: top 0.3s, font-size 0.3s, color 0.3s;
      }
    }
  }

  .i-have-read-and-block {
    margin-top: 36px;

    .checkbox-label {
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;

      .checkbox-label-wrapper {
        display: flex;
        align-items: center;
        position: relative;
        padding-left: 30px;

        input[type='checkbox'] {
          position: absolute;
          opacity: 0;
          cursor: pointer;
          height: 0;
          width: 0;
          border-radius: 2px;

          &:checked ~ .checkbox {
            background-color: #90fc3c;
            border-color: transparent;

            &::after {
              opacity: 1;
              visibility: visible;
            }
          }
        }

        .checkbox {
          margin-top: 1px;
          width: 18px;
          height: 18px;
          border: 1px solid #626165;
          border-radius: 2px;
          position: absolute;
          left: 0;
          top: 0;
          background-color: transparent;
          transition: background-color 0.3s, border-color 0.3s;

          &::after {
            content: '';
            width: 14px;
            height: 14px;
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
            background: url('~@/assets/icons/check-mark.svg') no-repeat center;
            background-size: contain;
            opacity: 0;
            visibility: hidden;
            filter: brightness(0);
            transition: opacity 0.3s, visibility 0.3s;
          }
        }

        .checkbox-text-container {
          line-height: 1.5;
          font-size: 14px;

          a {
            color: #4a87ff;

            &:hover {
              opacity: 0.5;
            }
          }
        }
      }
    }
  }

  .cta-container {
    margin-top: 26px;
    text-align: center;

    &.payout-method-selection {
      margin-top: 36px;
      padding-top: 24px;
      border-top: 1px solid #3c3a3e;
    }

    .selection-title {
      text-align: center;
      font-weight: 700;
      margin-bottom: 16px;
      color: #fff;
      font-size: 16px;
    }

    .buttons-special {
      display: flex;
      gap: 16px;
      justify-content: center;

      .button {
        flex: 1;
      }
    }

    .button {
      margin: 0 auto;
      width: 100%;
      max-width: 304px;
      background-color: #626165;
      color: #fff;
      font-size: 14px;
      font-weight: 700;

      &.red {
        background-color: #90fc3c;
        color: #131214;
      }
    }
  }

  label {
    position: relative;
    height: 50px;
    display: inline-block;
    width: 100%;
  }

  select {
    -moz-appearance: none;
    appearance: none;
    -webkit-appearance: none;
    height: 50px;
    background-color: transparent;
    border: 1px solid #626165;
    padding: 14px 15px 14px 16px;
    color: #fff;
    font-size: 14px;
    line-height: 1.5;
    width: 100%;
    cursor: pointer;

    &.empty {
      color: #88868d;
    }

    option {
      background-color: #2b2a2c;
      border: 1px solid #626165;
      color: #fff;
    }
  }

  .success-icon {
    text-align: center;
    margin-bottom: 24px;
  }

  .success-text {
    text-align: center;
    color: #fff;
    font-size: 16px;
    line-height: 1.5;
    margin-bottom: 24px;
  }

  .error-box {
    color: #ff7c7c;
    margin-top: 16px;
  }

  @media (max-width: 600px) {
    .form-fields-wrapper {
      grid-template-columns: 1fr;
    }
  }
</style>
